import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Search, Edit, Trash2, Layers, ChevronDown, ChevronRight, Box, Upload, Download } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import BulkImportDialog from "@/components/BulkImportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialFormData = {
  name: "",
  sku: "",
  description: "",
  quantity: 0,
  min_stock_level: 5,
  components: [],
};

export default function SubAssemblies() {
  const [subAssemblies, setSubAssemblies] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bomDialogOpen, setBomDialogOpen] = useState(false);
  const [editingSubAssembly, setEditingSubAssembly] = useState(null);
  const [subAssemblyToDelete, setSubAssemblyToDelete] = useState(null);
  const [selectedBom, setSelectedBom] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchSubAssemblies();
    fetchComponents();
  }, [search]);

  const fetchSubAssemblies = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);

      const response = await axios.get(`${API}/sub-assemblies?${params}`);
      setSubAssemblies(response.data);
    } catch (error) {
      console.error("Error fetching sub-assemblies:", error);
      toast.error("Failed to fetch sub-assemblies");
    } finally {
      setLoading(false);
    }
  };

  const fetchComponents = async () => {
    try {
      const response = await axios.get(`${API}/components`);
      setComponents(response.data);
    } catch (error) {
      console.error("Error fetching components:", error);
    }
  };

  const fetchBom = async (subAssemblyId) => {
    try {
      const response = await axios.get(`${API}/sub-assemblies/${subAssemblyId}/bom`);
      setSelectedBom(response.data);
      setBomDialogOpen(true);
    } catch (error) {
      console.error("Error fetching BOM:", error);
      toast.error("Failed to fetch Bill of Materials");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        components: formData.components.map(c => ({
          item_id: c.item_id,
          item_type: "component",
          quantity: c.quantity
        }))
      };

      if (editingSubAssembly) {
        await axios.put(`${API}/sub-assemblies/${editingSubAssembly.id}`, payload);
        toast.success("Sub-assembly updated successfully");
      } else {
        await axios.post(`${API}/sub-assemblies`, payload);
        toast.success("Sub-assembly created successfully");
      }
      setDialogOpen(false);
      setEditingSubAssembly(null);
      setFormData(initialFormData);
      fetchSubAssemblies();
    } catch (error) {
      console.error("Error saving sub-assembly:", error);
      toast.error(error.response?.data?.detail || "Failed to save sub-assembly");
    }
  };

  const handleEdit = (subAssembly) => {
    setEditingSubAssembly(subAssembly);
    setFormData({
      name: subAssembly.name,
      sku: subAssembly.sku,
      description: subAssembly.description || "",
      quantity: subAssembly.quantity,
      min_stock_level: subAssembly.min_stock_level,
      components: subAssembly.components || [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!subAssemblyToDelete) return;
    try {
      await axios.delete(`${API}/sub-assemblies/${subAssemblyToDelete.id}`);
      toast.success("Sub-assembly deleted successfully");
      setDeleteDialogOpen(false);
      setSubAssemblyToDelete(null);
      fetchSubAssemblies();
    } catch (error) {
      console.error("Error deleting sub-assembly:", error);
      toast.error("Failed to delete sub-assembly");
    }
  };

  const openNewDialog = () => {
    setEditingSubAssembly(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const addComponent = () => {
    setFormData({
      ...formData,
      components: [...formData.components, { item_id: "", item_type: "component", quantity: 1 }]
    });
  };

  const updateComponent = (index, field, value) => {
    const newComponents = [...formData.components];
    newComponents[index] = { ...newComponents[index], [field]: value };
    setFormData({ ...formData, components: newComponents });
  };

  const removeComponent = (index) => {
    setFormData({
      ...formData,
      components: formData.components.filter((_, i) => i !== index)
    });
  };

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getComponentName = (componentId) => {
    const comp = components.find(c => c.id === componentId);
    return comp ? comp.name : "Unknown";
  };

  const getStockStatus = (item) => {
    if (item.quantity === 0) return "critical";
    if (item.quantity <= item.min_stock_level) return "low";
    return "ok";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === subAssemblies.length && subAssemblies.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(subAssemblies.map(s => s.id)));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await axios.delete(`${API}/sub-assemblies/bulk`, { data: { ids: [...selectedIds] } });
      toast.success(`Deleted ${selectedIds.size} sub-assembly(ies)`);
      setSelectedIds(new Set());
      setBulkDeleteDialogOpen(false);
      fetchSubAssemblies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    }
  };

  const handleExport = async (fmt) => {
    try {
      const res = await axios.get(`${API}/export/sub-assemblies?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sub_assemblies.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="sub-assemblies-page">
      <div className="page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Sub-Assemblies</h1>
          <p className="page-subtitle">Manage sub-assemblies composed of components</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <Button variant="destructive" onClick={() => setBulkDeleteDialogOpen(true)} className="rounded-none uppercase text-xs tracking-wider">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedIds.size})
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-none uppercase text-xs tracking-wider">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("xlsx")}>Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("csv")}>CSV (.csv)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={() => setImportDialogOpen(true)} className="rounded-none uppercase text-xs tracking-wider">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button onClick={openNewDialog} className="rounded-none uppercase text-xs tracking-wider" data-testid="add-sub-assembly-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Sub-Assembly
          </Button>
        </div>
      </div>

      <div className="page-content">
        {/* Search */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-none"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Sub-Assemblies Table */}
        <Card className="rounded-none border-zinc-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading sub-assemblies...</div>
            ) : subAssemblies.length === 0 ? (
              <div className="p-8 text-center">
                <Layers className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No sub-assemblies found</p>
                <Button onClick={openNewDialog} variant="outline" className="mt-4 rounded-none">
                  Add your first sub-assembly
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="sub-assemblies-table">
                  <thead>
                    <tr>
                      <th className="w-8">
                        <Checkbox
                          checked={selectedIds.size === subAssemblies.length && subAssemblies.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </th>
                      <th className="w-8"></th>
                      <th>SKU</th>
                      <th>Name</th>
                      <th>Components</th>
                      <th className="text-right">Quantity</th>
                      <th className="text-right">Cost</th>
                      <th>Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subAssemblies.map((sa) => {
                      const status = getStockStatus(sa);
                      const isExpanded = expandedRows.has(sa.id);
                      return (
                        <>
                          <tr key={sa.id} data-testid={`sub-assembly-row-${sa.id}`}>
                            <td>
                              <Checkbox
                                checked={selectedIds.has(sa.id)}
                                onCheckedChange={() => toggleSelect(sa.id)}
                              />
                            </td>
                            <td>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => toggleRow(sa.id)}
                                data-testid={`expand-${sa.id}`}
                              >
                                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </Button>
                            </td>
                            <td>
                              <span className="sku-badge">{sa.sku}</span>
                            </td>
                            <td className="font-sans font-medium">{sa.name}</td>
                            <td>
                              <Badge variant="outline" className="rounded-none">
                                {sa.components?.length || 0} items
                              </Badge>
                            </td>
                            <td className="text-right">
                              <span className={`quantity-display ${status}`}>
                                {sa.quantity}
                              </span>
                            </td>
                            <td className="text-right cost-display">{formatCurrency(sa.calculated_cost)}</td>
                            <td>
                              <Badge
                                variant={status === "critical" ? "destructive" : status === "low" ? "outline" : "secondary"}
                                className={`rounded-none uppercase text-xs ${status === "low" ? "border-orange-500 text-orange-500" : ""}`}
                              >
                                {status === "critical" ? "OUT OF STOCK" : status === "low" ? "LOW STOCK" : "IN STOCK"}
                              </Badge>
                            </td>
                            <td className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => fetchBom(sa.id)}
                                  className="text-xs"
                                  data-testid={`view-bom-${sa.id}`}
                                >
                                  BOM
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEdit(sa)}
                                  data-testid={`edit-sub-assembly-${sa.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setSubAssemblyToDelete(sa);
                                    setDeleteDialogOpen(true);
                                  }}
                                  data-testid={`delete-sub-assembly-${sa.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && sa.components && sa.components.length > 0 && (
                            <tr className="bg-zinc-50">
                              <td colSpan={9} className="p-0">
                                <div className="p-4 pl-12">
                                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Components</p>
                                  <div className="space-y-1">
                                    {sa.components.map((comp, idx) => (
                                      <div key={idx} className="flex items-center gap-2 text-sm">
                                        <Box className="w-3 h-3 text-muted-foreground" />
                                        <span className="font-mono text-xs">{getComponentName(comp.item_id)}</span>
                                        <span className="text-muted-foreground">×</span>
                                        <span className="font-mono">{comp.quantity}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="sub-assembly-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold uppercase tracking-wider">
              {editingSubAssembly ? "Edit Sub-Assembly" : "Add Sub-Assembly"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6 py-4">
              {/* Basic Info */}
              <div className="form-section">
                <div className="form-section-header">Basic Information</div>
                <div className="form-section-content grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="rounded-none"
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sku">SKU *</Label>
                    <Input
                      id="sku"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                      required
                      placeholder="SA-[CAT]-001"
                      className="rounded-none font-mono"
                      data-testid="input-sku"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="rounded-none"
                      rows={2}
                      data-testid="input-description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantity</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                      className="rounded-none font-mono"
                      data-testid="input-quantity"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="min_stock_level">Min Stock Level</Label>
                    <Input
                      id="min_stock_level"
                      type="number"
                      min="0"
                      value={formData.min_stock_level}
                      onChange={(e) => setFormData({ ...formData, min_stock_level: parseInt(e.target.value) || 0 })}
                      className="rounded-none font-mono"
                      data-testid="input-min-stock"
                    />
                  </div>
                </div>
              </div>

              {/* Components */}
              <div className="form-section">
                <div className="form-section-header flex items-center justify-between">
                  <span>Components</span>
                  <Button type="button" variant="outline" size="sm" onClick={addComponent} className="rounded-none" data-testid="add-component-btn">
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
                <div className="form-section-content space-y-3">
                  {formData.components.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No components added yet</p>
                  ) : (
                    formData.components.map((comp, index) => (
                      <div key={index} className="flex gap-2 items-end">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs">Component</Label>
                          <Select
                            value={comp.item_id}
                            onValueChange={(value) => updateComponent(index, "item_id", value)}
                          >
                            <SelectTrigger className="rounded-none" data-testid={`select-component-${index}`}>
                              <SelectValue placeholder="Select component" />
                            </SelectTrigger>
                            <SelectContent>
                              {components.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.name} ({c.sku})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-24 space-y-1">
                          <Label className="text-xs">Qty</Label>
                          <Input
                            type="number"
                            min="1"
                            value={comp.quantity}
                            onChange={(e) => updateComponent(index, "quantity", parseInt(e.target.value) || 1)}
                            className="rounded-none font-mono"
                            data-testid={`input-comp-qty-${index}`}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeComponent(index)}
                          data-testid={`remove-component-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                Cancel
              </Button>
              <Button type="submit" className="rounded-none uppercase tracking-wider" data-testid="save-sub-assembly-btn">
                {editingSubAssembly ? "Update" : "Create"} Sub-Assembly
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* BOM Dialog */}
      <Dialog open={bomDialogOpen} onOpenChange={setBomDialogOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid="bom-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold uppercase tracking-wider">
              Bill of Materials
            </DialogTitle>
          </DialogHeader>
          {selectedBom && (
            <div className="py-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-xs uppercase tracking-wider text-muted-foreground">Component</th>
                    <th className="text-right py-2 text-xs uppercase tracking-wider text-muted-foreground">Qty</th>
                    <th className="text-right py-2 text-xs uppercase tracking-wider text-muted-foreground">Unit Cost</th>
                    <th className="text-right py-2 text-xs uppercase tracking-wider text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedBom.bom.map((item, idx) => (
                    <tr key={idx} className="border-b border-zinc-100">
                      <td className="py-2">
                        <span className="font-medium">{item.name}</span>
                        <span className="text-xs text-muted-foreground ml-2 font-mono">{item.sku}</span>
                      </td>
                      <td className="text-right font-mono">{item.quantity}</td>
                      <td className="text-right font-mono">{formatCurrency(item.unit_cost)}</td>
                      <td className="text-right font-mono font-medium">{formatCurrency(item.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-50">
                    <td colSpan={3} className="py-2 font-semibold uppercase text-xs tracking-wider">Total Cost</td>
                    <td className="text-right py-2 font-mono font-semibold">{formatCurrency(selectedBom.total_cost)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Sub-Assembly(ies)</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedIds.size} selected sub-assembly(ies). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="rounded-none bg-destructive">
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-none" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sub-Assembly</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{subAssemblyToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-none bg-destructive" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        type="sub-assemblies"
        onSuccess={fetchSubAssemblies}
      />
    </div>
  );
}
